import { useCallback, useState } from 'react'
import { MessageSimple, MessageTimestamp } from 'stream-chat-react'
import Box from '@mui/material/Box'
import { useMessageContext } from 'stream-chat-react'
import GlobalStyles from '@mui/material/GlobalStyles'
import ReclipMessage from './ReclipMessage'
import { isReclipMessage } from '../../reclips/utils'
import Loader from 'components/core/Loader'
import api from 'services/api'

import './CustomMessage.css'

// width of area where staff members' names are listed next to messages
const GUTTER_WIDTH = 180

const CustomMessage = (props) => {
  const [isSendingFeedback, setIsSendingFeedback] = useState(false)
  const { message, isMyMessage, firstOfGroup, groupedByUser } =
    useMessageContext()
  const { staff_member, isSystemMessage, systemMessageData } = message
  const mine = isMyMessage()
  const reclip = isReclipMessage(message)

  if (isSystemMessage) console.log(message)

  const postToFeedback = useCallback(async () => {
    setIsSendingFeedback(true)

    const reclipLink = (() => {
      if (
        message.attachments &&
        message.attachments.length > 0 &&
        message.attachments[0].isReclipPlaybackAttachment
      ) {
        const { title } = message.attachments[0]
        const source_id = title.replace('.m4a', '')
        return window.location.origin + `/r/${source_id}`
      } else {
        return null
      }
    })()

    const text = reclipLink ? `${message.text}: ${reclipLink}` : message.text

    await api.chat.postToFeedback({
      title: `Feedback from ${message.user.name} (@${message.user.username})`,
      message: text,
    })

    setIsSendingFeedback(false)
  }, [message])

  const messageEl = (() => {
    if (isSystemMessage && systemMessageData.messageType === 'replyToStory') {
      return (
        <Box sx={{ fontSize: '0.9em', mb: 1 }}>
          Replying to{' '}
          <Box
            sx={{
              display: 'inline-block',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
            onClick={() =>
              window.open(
                window.origin + '/s/' + systemMessageData.storyShareCode,
                '_blank'
              )
            }
          >
            {systemMessageData.storyDisplayTitle}
          </Box>
          <br />
          on the Team Reclip story.
        </Box>
      )
    } else if (
      isSystemMessage &&
      systemMessageData.messageType === 'invisibleMessage'
    ) {
      return null
    } else if (reclip) {
      return <ReclipMessage />
    } else {
      return <MessageSimple {...props} />
    }
  })()

  const gutterStyles = {
    width: `${GUTTER_WIDTH}px`,
    textAlign: 'center',
    position: 'absolute',
    bottom: '11px',
    transform: 'translateX(10px)',
    left: '100%',
    fontSize: 12,
    wordBreak: 'break-word',
    color: (theme) => theme.palette.text.secondary,
    px: 2,
    lineHeight: '12px',
    cursor: 'default',
  }

  // A phone has no room for the gutter. A sent message has when it was sent
  // over it, once per run of messages, and under each the staff member who
  // wrote it with the read avatar or check after the name; the line is kept
  // for the avatar when no name is set. #feedback goes to the far side of
  // the user's message.
  const staffStyles = {
    ...gutterStyles,
    position: { mobile: 'static', desktop: 'absolute' },
    width: { mobile: 'auto', desktop: `${GUTTER_WIDTH}px` },
    minHeight: { mobile: 12, desktop: 0 },
    textAlign: { mobile: 'right', desktop: 'center' },
    transform: { mobile: 'none', desktop: 'translateX(10px)' },
    pl: { mobile: 1, desktop: 2 },
    pr: { mobile: '30px', desktop: 2 },
  }
  const feedbackStyles = {
    ...gutterStyles,
    width: { mobile: 'auto', desktop: `${GUTTER_WIDTH}px` },
    top: { mobile: '50%', desktop: 'auto' },
    bottom: { mobile: 'auto', desktop: '11px' },
    left: { mobile: 'auto', desktop: '100%' },
    right: { mobile: 0, desktop: 'auto' },
    transform: { mobile: 'translateY(-50%)', desktop: 'translateX(10px)' },
    px: { mobile: 1, desktop: 2 },
    py: { mobile: 1, desktop: 0 },
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  }

  return (
    <Box
      sx={(theme) => ({
        position: 'relative',
        '& .str-chat__message-actions-box': {
          backgroundImage: 'none !important',
          backgroundColor: `${theme.palette.background.paper} !important`,
        },
        '& .str-chat__modal__close-button': {
          display: 'none',
        },
        '& .str-chat__message-text-inner': {
          backgroundColor: `${theme.palette.background.paper} !important`,
          border: 'none !important',
          minHeight: 'none !important',
          px: 2,
          py: 1,
        },
        // Stream's time line under a sent message, and its read avatar or
        // check on the message's corner, move to the lines above and below.
        ...(mine && {
          [theme.breakpoints.down('desktop')]: {
            '& .str-chat__message-simple-data': { display: 'none' },
            '& .str-chat__message-simple-status': {
              bottom: '-15px',
              margin: 0,
            },
          },
        }),
      })}
    >
      {/* A clip carries its own time line. */}
      {mine && !isSystemMessage && !reclip && (!groupedByUser || firstOfGroup) && (
        <Box
          sx={{
            display: { mobile: 'block', desktop: 'none' },
            textAlign: 'right',
            fontSize: 10,
            color: 'text.secondary',
            px: 1,
            pt: 1,
            pb: 0.5,
          }}
        >
          <MessageTimestamp calendar />
        </Box>
      )}
      {messageEl}
      {(staff_member || mine) && (
        <Box className="chat-gutter" sx={staffStyles}>
          {staff_member?.replace('@reclip.app', '')}
        </Box>
      )}
      <GlobalStyles
        styles={(theme) => ({
          [theme.breakpoints.up('desktop')]: {
            '.messaging.str-chat .str-chat__list': {
              paddingRight: `${GUTTER_WIDTH}px`,
            },
          },
        })}
      />
      {!mine && !isSystemMessage && (
        <Box
          className="chat-gutter"
          sx={feedbackStyles}
          onClick={postToFeedback}
        >
          {isSendingFeedback ? <Loader size={12} /> : '#feedback'}
        </Box>
      )}
    </Box>
  )
}

export default CustomMessage
